import React, { useState, useEffect } from 'react';
import { Star, ExternalLink, MapPin } from 'lucide-react';
import { businessInfo, customerReviews as mockReviews } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/public/reviews`);
      const data = await response.json();
      if (data.reviews && data.reviews.length > 0) {
        setReviews(data.reviews);
      } else {
        // Fallback to mock reviews if no reviews in database
        setReviews(mockReviews);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      // Fallback to mock reviews on error
      setReviews(mockReviews);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex space-x-1">
        {[...Array(5)].map((_, index) => (
          <Star 
            key={index}
            size={16}
            className={index < rating ? 'fill-amber-500 text-amber-500' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  return (
    <section id="reviews" className="py-6 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-4">
          <h2 className="text-2xl md:text-3xl font-bold text-amber-900 mb-1">
            Customer Reviews
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm mb-3">
            What our guests say about Marina Homes.
          </p>

          {/* Review Platform Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
            <a 
              href={businessInfo.googleMapsReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:scale-105 shadow-sm"
            >
              <MapPin size={14} className="text-red-500" />
              <span>Google Maps</span>
            </a>
            <a 
              href={businessInfo.airbnbReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-pink-500 to-red-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:scale-105 shadow-sm"
            >
              <span>Airbnb</span>
              <ExternalLink size={12} />
            </a>
          </div>
        </div>

        {/* Reviews Grid */}
        <div>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reviews.slice(0, 6).map((review) => (
              <div 
                key={review.id}
                className="bg-gradient-to-br from-amber-50 to-white p-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 border border-amber-100"
              >
                {/* Avatar and Name */}
                <div className="flex items-center space-x-3 mb-2">
                  {review.avatarImage ? (
                    <img 
                      src={review.avatarImage}
                      alt={review.name}
                      className="w-10 h-10 rounded-full object-cover border-2 border-amber-200"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-amber-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {review.avatar}
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900 text-sm">{review.name}</h3>
                      {review.platform && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          review.platform === 'Google' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-pink-100 text-pink-700'
                        }`}>
                          {review.platform}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{review.date}</p>
                  </div>
                </div>

                {/* Rating */}
                <div className="mb-2">
                  {renderStars(review.rating)}
                </div>

                {/* Comment */}
                <p className="text-gray-700 leading-relaxed text-xs line-clamp-3">
                  {review.comment}
                </p>
              </div>
            ))}
          </div>
          )}
        </div>

        {/* Call to Action - Leave Review */}
        <div className="text-center bg-gradient-to-r from-amber-600 to-amber-700 rounded-lg p-4 mt-4">
          <h3 className="text-lg font-bold text-white mb-1">
            Had a Great Experience?
          </h3>
          <p className="text-amber-100 text-xs mb-3">
            Share your experience and help other travelers discover Marina Homes.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <a 
              href={businessInfo.googleMapsReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-1 bg-white text-amber-700 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:scale-105 shadow"
            >
              <MapPin size={14} />
              <span>Google Review</span>
            </a>
            <a 
              href={businessInfo.airbnbReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-1 bg-pink-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:scale-105 shadow"
            >
              <span>Airbnb Review</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
